import { defineStore } from 'pinia'

import { authApi } from '@/services/auth'
import { tokenStorage } from '@/services/tokenStorage'

function extractError(error) {
  const data = error.response?.data
  if (!data) return 'login.failed'
  if (typeof data === 'string') return data
  if (data.detail) return data.detail

  const firstError = Object.values(data).find(
    (value) => Array.isArray(value) && value.length > 0,
  )
  if (firstError) return firstError[0]

  return 'login.failed'
}

export const useAuthStore = defineStore('auth', {
  state: () => ({
    token: null,
    ssoToken: null,
    user: null,
    loading: false,
    error: null,
  }),

  getters: {
    isAuthenticated: (state) => Boolean(state.token),
    displayName: (state) => {
      if (!state.user) return ''
      const full = `${state.user.last_name || ''} ${state.user.first_name || ''}`.trim()
      return full || state.user.username
    },
    access: (state) => {
      const roles = state.user?.role || []
      const rawDastresi = state.user?.profile?.dastresi
      const dastresi = Array.isArray(rawDastresi)
        ? rawDastresi
        : rawDastresi
          ? [rawDastresi]
          : []
      // Roles and permissions of the club application coming from the auth
      // service (e.g. ``manage`` / ``manage_maghta``).
      const appRoles = state.user?.app_roles || []
      const permissions = state.user?.permissions || []
      return [...roles, ...dastresi, ...appRoles, ...permissions]
    },
  },

  actions: {
    restore() {
      this.token = tokenStorage.get()
      this.ssoToken = tokenStorage.getSso()
      this.user = tokenStorage.getUser()
    },

    async login(username, password, remember = true) {
      this.loading = true
      this.error = null
      try {
        const data = await authApi.login(username, password)
        this.applySession(data, remember)
        return true
      } catch (error) {
        this.error = extractError(error)
        return false
      } finally {
        this.loading = false
      }
    },

    /** Store the payload shared by the password and SSO logins. */
    applySession(data, remember = true) {
      this.token = data.token
      this.ssoToken = data.sso_token
      this.user = data.user
      tokenStorage.set(data.token, data.sso_token, data.user, remember)
    },

    /**
     * Log in with an SSO token from the ``/custom_login/<token>`` callback.
     */
    async ssoLogin(token, remember = true) {
      this.loading = true
      this.error = null
      try {
        const data = await authApi.sso(token)
        this.applySession(data, remember)
        return true
      } catch (error) {
        this.error = extractError(error)
        return false
      } finally {
        this.loading = false
      }
    },

    async fetchMe() {
      if (!this.token) return
      try {
        this.user = await authApi.me()
        tokenStorage.setUser(this.user)
      } catch {
        /* handled globally by the 401 interceptor */
      }
    },

    clear() {
      this.token = null
      this.ssoToken = null
      this.user = null
      tokenStorage.clear()
    },

    async logout() {
      try {
        await authApi.logout()
      } catch {
        /* token may already be invalid */
      }
      this.clear()
    },
  },
})
